import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, Search, X, Package, Check, Minus, Plus } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BUNDLE_PRICE = 5.00;
const MAX_ITEMS = 10;

const BundleBuilder = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(true);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoadingProducts(true);
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoadingProducts(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const filteredProducts = products.filter(product => {
    const matchesCategory = !selectedCategory || product.category === selectedCategory;
    const matchesSearch = !searchQuery || 
      product.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const toggleProduct = (product) => {
    const isSelected = selectedProducts.find(p => p.id === product.id);
    
    if (isSelected) {
      setSelectedProducts(selectedProducts.filter(p => p.id !== product.id));
    } else {
      if (selectedProducts.length >= MAX_ITEMS) {
        toast.error(`You can only select up to ${MAX_ITEMS} formulas in a bundle`);
        return;
      }
      setSelectedProducts([...selectedProducts, product]);
    }
  };

  const removeProduct = (productId) => {
    setSelectedProducts(selectedProducts.filter(p => p.id !== productId));
  };

  const handleCheckout = async () => {
    if (selectedProducts.length === 0) {
      toast.error('Please select at least one formula for your bundle');
      return;
    }

    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/bundle/checkout`, {
        product_ids: selectedProducts.map(p => p.id),
        email: email,
        callback_url: `${originUrl}/success`
      });

      const { authorization_url } = response.data;
      window.location.href = authorization_url;
    } catch (error) {
      console.error('Error creating bundle checkout:', error);
      toast.error(error.response?.data?.detail || 'Failed to create checkout. Please try again.');
      setLoading(false);
    }
  };

  // Calculate savings
  const totalIndividualPrice = selectedProducts.reduce((sum, p) => sum + p.price, 0);
  const savings = totalIndividualPrice - BUNDLE_PRICE;
  const savingsPercent = totalIndividualPrice > 0 ? Math.round((savings / totalIndividualPrice) * 100) : 0;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center gap-2">
              <ShoppingBag className="h-8 w-8 text-slate-900" />
              <span className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Formula Library</span>
            </Link>
            <nav className="hidden md:flex gap-8 items-center">
              <Link to="/products" className="text-slate-600 hover:text-slate-900 font-medium transition-colors">
                Products
              </Link>
              <Link to="/subscriptions" className="text-slate-600 hover:text-slate-900 font-medium transition-colors">
                Subscriptions
              </Link>
              <Link to="/" className="text-slate-600 hover:text-slate-900 font-medium transition-colors">
                Home
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
            <Package className="h-5 w-5" />
            <span className="font-medium">Build Your Own Bundle</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="bundle-title">
            Choose Any 10 Formulas for Just $5
          </h1>
          <p className="text-lg sm:text-xl text-emerald-100 max-w-2xl mx-auto">
            Mix and match from our entire library of 1,200+ formulas. Pick your favorites and save big!
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Product Selection */}
          <div className="lg:col-span-2">
            {/* Filters */}
            <div className="bg-white rounded-2xl border border-slate-200 p-4 mb-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <Input
                      type="text"
                      placeholder="Search formulas..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 h-12 rounded-lg border-slate-200"
                      data-testid="bundle-search"
                    />
                  </div>
                </div>
                <div className="sm:w-64">
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full h-12 px-4 rounded-lg border border-slate-200 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    data-testid="bundle-category-filter"
                  >
                    <option value="">All Categories</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Products Grid */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Available Formulas
                </h2>
                <span className="text-sm text-slate-500">
                  {filteredProducts.length} formulas found
                </span>
              </div>

              {loadingProducts ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
                  <p className="mt-4 text-slate-600">Loading formulas...</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-[600px] overflow-y-auto pr-2">
                  {filteredProducts.map((product) => {
                    const isSelected = selectedProducts.find(p => p.id === product.id);
                    return (
                      <div
                        key={product.id}
                        onClick={() => toggleProduct(product)}
                        className={`relative border rounded-xl p-4 cursor-pointer transition-all ${
                          isSelected 
                            ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-500' 
                            : 'border-slate-200 hover:border-emerald-300 hover:bg-slate-50'
                        }`}
                        data-testid={`bundle-product-${product.id}`}
                      >
                        {isSelected && (
                          <div className="absolute top-2 right-2 bg-emerald-500 text-white rounded-full p-1">
                            <Check className="h-4 w-4" />
                          </div>
                        )}
                        <div className="flex gap-3">
                          <img
                            src={product.image_url}
                            alt={product.title}
                            className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-emerald-600 font-medium mb-1">{product.category}</p>
                            <h3 className="font-semibold text-slate-900 text-sm line-clamp-2 mb-1">
                              {product.title}
                            </h3>
                            <p className="text-sm text-slate-500" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                              ${product.price.toFixed(2)}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Bundle Summary - Sticky Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white p-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      Your Bundle
                    </h3>
                    <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-medium">
                      {selectedProducts.length}/{MAX_ITEMS}
                    </span>
                  </div>
                  <div className="w-full bg-white/20 rounded-full h-2">
                    <div 
                      className="bg-white rounded-full h-2 transition-all duration-300"
                      style={{ width: `${(selectedProducts.length / MAX_ITEMS) * 100}%` }}
                    />
                  </div>
                </div>

                {/* Selected Products */}
                <div className="p-4 max-h-64 overflow-y-auto">
                  {selectedProducts.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Package className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                      <p>No formulas selected yet</p>
                      <p className="text-sm">Click on formulas to add them</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {selectedProducts.map((product) => (
                        <div key={product.id} className="flex items-center gap-2 bg-slate-50 rounded-lg p-2">
                          <img
                            src={product.image_url}
                            alt={product.title}
                            className="w-10 h-10 object-cover rounded"
                          />
                          <span className="flex-1 text-sm text-slate-700 line-clamp-1">
                            {product.title}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeProduct(product.id);
                            }}
                            className="text-slate-400 hover:text-red-500 transition-colors"
                            data-testid={`remove-bundle-item-${product.id}`}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Pricing */}
                <div className="border-t border-slate-200 p-4 bg-slate-50">
                  {selectedProducts.length > 0 && (
                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between text-sm text-slate-600">
                        <span>Individual total:</span>
                        <span className="line-through">${totalIndividualPrice.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm text-emerald-600 font-medium">
                        <span>You save:</span>
                        <span>${savings.toFixed(2)} ({savingsPercent}%)</span>
                      </div>
                    </div>
                  )}
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-lg font-bold text-slate-900">Bundle Price:</span>
                    <span className="text-3xl font-bold text-emerald-600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      $5.00
                    </span>
                  </div>
                </div>

                {/* Checkout */}
                <div className="p-4 border-t border-slate-200">
                  <div className="mb-4">
                    <Label htmlFor="bundle-email" className="text-slate-700 mb-2 block text-sm">
                      Email Address
                    </Label>
                    <Input
                      id="bundle-email"
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="h-12 rounded-lg border-slate-200"
                      disabled={loading}
                      data-testid="bundle-email-input"
                    />
                  </div>
                  <Button
                    onClick={handleCheckout}
                    disabled={loading || selectedProducts.length === 0}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-full h-12 text-lg font-medium shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="bundle-checkout-btn"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        Processing...
                      </span>
                    ) : (
                      `Checkout Bundle - $5.00`
                    )}
                  </Button>
                  <p className="text-xs text-slate-500 text-center mt-3">
                    Secure payment via Paystack
                  </p>
                </div>
              </div>

              {/* Benefits */}
              <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                <h4 className="font-bold text-emerald-800 mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Bundle Benefits
                </h4>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2 text-sm text-emerald-700">
                    <Check className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>Choose any 10 formulas from our library</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-emerald-700">
                    <Check className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>Instant PDF download after payment</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-emerald-700">
                    <Check className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>Mix categories - cosmetics, cleaning, more</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-emerald-700">
                    <Check className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>24/7 WhatsApp support included</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BundleBuilder;
